import Toaster from '../components/common/Toaster';
import { getConfigurationKey } from './auth';
import { SIMULATION_MAX_DIGITS_BEFORE_DECIMAL, SIMULATION_MAX_DIGITS_AFTER_DECIMAL } from '../config/constants';

/**
 * Common validation function for revised rates in simulation components
 * @param {string|number} cellValue - The value to validate
 * @param {Object} props - The cell props containing row data
 * @param {string} type - The type of field being validated ('basicRate', 'scrapRate', 'percentage', etc.)
 * @param {Object} options - Additional validation options
 * @returns {Object} - Validation result with isValid, value, and message
 */
export const validateRevisedRate = (cellValue, props, type, options = {}) => {
    const row = props?.valueFormatted ? props.valueFormatted : props?.data;
    const config = getConfigurationKey();
    // For ALL simulation field types: 8 digits before decimal, 6 after decimal (non-impacted data)
    const maxDigitsBeforeDecimal = SIMULATION_MAX_DIGITS_BEFORE_DECIMAL;
    const maxDecimalPlaces = SIMULATION_MAX_DIGITS_AFTER_DECIMAL;

    // IMPORTANT: For otherCost and conditionCost types, allow negative values (discounts)
    const allowNegative = type === 'otherCost';
    // Convert cellValue to string for processing
    const stringValue = String(cellValue || '');

    // Check if value is empty
    if (!stringValue || stringValue.trim() === '') {
        return {
            isValid: true,
            value: '',
            message: ''
        };
    }

    // Check for valid number format (allow negative sign if allowNegative is true)
    const numberRegex = allowNegative
        ? /^[+-]?([0-9]+(?:[.][0-9]*)?|\.[0-9]+)$/
        : /^[+]?([0-9]+(?:[.][0-9]*)?|\.[0-9]+)$/;

    if (!numberRegex.test(stringValue)) {
        const message = allowNegative
            ? 'Please enter a valid number.'
            : 'Please enter a valid positive number.';
        return {
            isValid: false,
            value: getLastValidValue(row, type),
            message: message
        };
    }

    // Parse the number
    const numericValue = parseFloat(stringValue);

    // Check if value is negative (only if negative values are not allowed)
    if (!allowNegative && numericValue < 0) {
        const message = 'Please enter a positive number.';
        return {
            isValid: false,
            value: getLastValidValue(row, type),
            message: message
        };
    }
    
    // Check and trim decimal places
    const decimalParts = stringValue.split('.');
    let trimmedValue = stringValue;
    let wasTrimmed = false;
    let trimMessage = '';

    if (decimalParts.length > 1) {
        const decimalPart = decimalParts[1];
        if (decimalPart.length > maxDecimalPlaces) {
            // Trim excess decimal places instead of rejecting
            const trimmedDecimal = decimalPart.substring(0, maxDecimalPlaces);
            trimmedValue = `${decimalParts[0]}.${trimmedDecimal}`;
            wasTrimmed = true;
            trimMessage = `Decimal places trimmed to ${maxDecimalPlaces} digits.`;
        }
    }

    // Check and trim digits before decimal
    const trimmedDecimalParts = trimmedValue.split('.');
    const integerPart = trimmedDecimalParts[0];
    if (integerPart.length > maxDigitsBeforeDecimal) {
        // Trim excess digits before decimal instead of rejecting
        const trimmedInteger = integerPart.substring(integerPart.length - maxDigitsBeforeDecimal);
        trimmedValue = trimmedDecimalParts.length > 1
            ? `${trimmedInteger}.${trimmedDecimalParts[1]}`
            : trimmedInteger;
        wasTrimmed = true;
        trimMessage = trimMessage
            ? `Value trimmed to ${maxDigitsBeforeDecimal} digits before decimal and ${maxDecimalPlaces} after.`
            : `Digits before decimal trimmed to ${maxDigitsBeforeDecimal}.`;
    }

    // If value was trimmed, show warning and return trimmed value
    if (wasTrimmed) {
        return {
            isValid: true,
            value: parseFloat(trimmedValue),
            message: trimMessage,
            wasTrimmed: true
        };
    }
    
    // For basic rate and scrap rate, check if scrap rate is greater than basic rate
    if (type === 'basicRate' || type === 'scrapRate') {
        const basicRateValidation = validateBasicRateVsScrapRate(row, type, numericValue);
        if (!basicRateValidation.isValid) {
            return basicRateValidation;
        }
    }
    
    return {
        isValid: true,
        value: numericValue,
        message: ''
    };
};

/**
 * Validate basic rate vs scrap rate relationship
 * @param {Object} row - Row data
 * @param {string} type - Field type being validated
 * @param {number} currentValue - Current value being validated
 * @returns {Object} - Validation result
 */
const validateBasicRateVsScrapRate = (row, type, currentValue) => {
    let basicRate, scrapRate;

    if (type === 'basicRate') {
        basicRate = currentValue;
        scrapRate = getEffectiveScrapRate(row);
    } else if (type === 'scrapRate') {
        basicRate = getEffectiveBasicRate(row);
        scrapRate = currentValue;
    }

    // Only validate if both values are positive numbers
    // Ignore validation if either value is 0, null, undefined, or NaN
    if (basicRate !== undefined && scrapRate !== undefined &&
        !isNaN(basicRate) && !isNaN(scrapRate) &&
        basicRate > 0 && scrapRate > 0 &&
        scrapRate > basicRate) {

       

        const message = 'Scrap Rate should be less than Basic Rate';
        return {
            isValid: false,
            value: getLastValidValue(row, type),
            message: message
        };
    }

    return {
        isValid: true,
        value: currentValue,
        message: ''
    };
};

/**
 * Get the effective basic rate from row data
 * @param {Object} row - Row data
 * @returns {number} - Effective basic rate
 */
const getEffectiveBasicRate = (row) => {
    // Priority 1: Use percentage-calculated basic rate if percentage exists
    if (row?.NewBasicrateFromPercentage !== undefined && row?.NewBasicrateFromPercentage !== null && !isNaN(row?.NewBasicrateFromPercentage)) {
        return Number(row?.NewBasicrateFromPercentage);
    }
    // Priority 2: Use manually entered basic rate
    else if (row?.NewBasicRate !== undefined && row?.NewBasicRate !== null && !isNaN(row?.NewBasicRate) && Number(row?.NewBasicRate) !== 0) {
        return Number(row?.NewBasicRate);
    }
    // Priority 3: Use NewBasicRatePerUOM if available
    else if (row?.NewBasicRatePerUOM !== undefined && row?.NewBasicRatePerUOM !== null && !isNaN(row?.NewBasicRatePerUOM) && Number(row?.NewBasicRatePerUOM) !== 0) {
        return Number(row?.NewBasicRatePerUOM);
    }
    // Priority 4: Use original basic rate
    else {
        return Number(row?.BasicRatePerUOM || 0);
    }
};

/**
 * Get the effective scrap rate from row data
 * @param {Object} row - Row data
 * @returns {number} - Effective scrap rate
 */
const getEffectiveScrapRate = (row) => {
    // Priority 1: Use NewScrapRate if it exists and is valid
    if (row?.NewScrapRate !== undefined && row?.NewScrapRate !== null && !isNaN(row?.NewScrapRate) && Number(row?.NewScrapRate) !== 0) {
        return Number(row?.NewScrapRate);
    }
    // Priority 2: Use NewScrapRatePerScrapUOM if available
    else if (row?.NewScrapRatePerScrapUOM !== undefined && row?.NewScrapRatePerScrapUOM !== null && !isNaN(row?.NewScrapRatePerScrapUOM) && Number(row?.NewScrapRatePerScrapUOM) !== 0) {
        return Number(row?.NewScrapRatePerScrapUOM);
    }
    // Priority 3: Use original scrap rate
    else {
        return Number(row?.ScrapRate || 0);
    }
};

/**
 * Get the last valid value for a field type
 * @param {Object} row - Row data
 * @param {string} type - Field type
 * @returns {number|string} - Last valid value
 */
const getLastValidValue = (row, type) => {
    switch (type) {
        case 'basicRate':
            // Priority 1: Use percentage-calculated basic rate if it exists
            if (row?.NewBasicrateFromPercentage !== undefined && row?.NewBasicrateFromPercentage !== null) {
                return Number(row?.NewBasicrateFromPercentage);
            }
            // Priority 2: Use manually entered basic rate if it exists and is valid
            else if (row?.NewBasicRate && Number(row?.NewBasicRate) !== 0) {
                return Number(row?.NewBasicRate);
            }
            // Priority 3: Use original basic rate
            else {
                return Number(row?.BasicRatePerUOM || 0);
            }
        case 'scrapRate':
            // Priority 1: Use NewScrapRate if it exists and is valid
            if (row?.NewScrapRate !== undefined && row?.NewScrapRate !== null && !isNaN(row?.NewScrapRate) && Number(row?.NewScrapRate) !== 0) {
                return Number(row?.NewScrapRate);
            }
            // Priority 2: Use NewScrapRatePerScrapUOM if available
            else if (row?.NewScrapRatePerScrapUOM !== undefined && row?.NewScrapRatePerScrapUOM !== null && !isNaN(row?.NewScrapRatePerScrapUOM) && Number(row?.NewScrapRatePerScrapUOM) !== 0) {
                return Number(row?.NewScrapRatePerScrapUOM);
            }
            // Priority 3: Use original scrap rate
            else {
                return Number(row?.ScrapRate || 0);
            }
        case 'percentage':
            // Return the current percentage if it exists and is valid, otherwise 0
            return Number(row?.Percentage || 0);
        case 'machineRate':
            // Priority 1: Use NewMachineRate if it exists and is valid
            if (row?.NewMachineRate && Number(row?.NewMachineRate) !== 0) {
                return Number(row?.NewMachineRate);
            }
            // Priority 2: Use original machine rate
            else {
                return Number(row?.MachineRate || 0);
            }
        case 'exchangeRate':
            // Priority 1: Use NewCurrencyExchangeRate if it exists and is valid
            if (row?.NewCurrencyExchangeRate && Number(row?.NewCurrencyExchangeRate) !== 0) {
                return Number(row?.NewCurrencyExchangeRate);
            }
            // Priority 2: Use original exchange rate
            else {
                return Number(row?.CurrencyExchangeRate || 0);
            }
        case 'operationRate':
            // Priority 1: Use NewRate if it exists and is valid
            if (row?.NewRate && Number(row?.NewRate) !== 0) {
                return Number(row?.NewRate);
            }
            // Priority 2: Use original rate
            else {
                return Number(row?.Rate || 0);
            }
        case 'operationBasicRate':
            // Priority 1: Use NewOperationBasicRate if it exists and is valid
            if (row?.NewOperationBasicRate && Number(row?.NewOperationBasicRate) !== 0) {
                return Number(row?.NewOperationBasicRate);
            }
            // Priority 2: Use original operation basic rate
            else {
                return Number(row?.OperationBasicRate || 0);
            }
        case 'otherCost':
            // For other cost, return the existing value (can be negative for discounts)
            return Number(row?.OldNetConditionCost || row?.OtherNetCost || 0);
        case 'conditionCost':
            // For condition cost, return the existing value (can be negative for discounts)
            return Number(row?.OldNetConditionCost || row?.NetConditionCost || 0);
        default:
            return 0;
    }
};

/**
 * Enhanced beforeSaveCell function that uses the common validation
 * @param {string|number} cellValue - The cell value to validate
 * @param {Object} props - Cell props
 * @param {string} type - Field type
 * @param {Object} options - Additional options
 * @returns {boolean} - Whether the value is valid
 */
export const beforeSaveCell = (cellValue, props, type, options = {}) => {
    const validation = validateRevisedRate(cellValue, props, type, options);

    // Only show toast message during user input (not during rendering/display)
    // Check if this is being called from a cell edit event (has api and node)
    const isUserInput = props?.api && props?.node && typeof props.api.setValue === 'function';

    if (validation.message && isUserInput) {
        Toaster.warning(validation.message);
    }

    if (!validation.isValid) {
        // Reset the field to last valid value in both row data and grid
        const row = props?.valueFormatted ? props.valueFormatted : props?.data;
        if (row) {
            switch (type) {
                case 'basicRate':
                    row.NewBasicRate = validation.value;
                    row.NewBasicRatePerUOM = validation.value;
                    row.NewBasicrateFromPercentage = undefined;
                    row.Percentage = 0;
                    break;
                case 'scrapRate':
                    row.NewScrapRate = validation.value;
                    // Also update NewScrapRatePerScrapUOM if IsScrapUOMApply is 'Yes'
                    if (row.IsScrapUOMApply === 'Yes' && row.CalculatedFactor) {
                        row.NewScrapRatePerScrapUOM = validation.value / row.CalculatedFactor;
                    }
                    break;
                case 'percentage':
                    row.Percentage = validation.value;
                    break;
                case 'machineRate':
                    row.NewMachineRate = validation.value;
                    break;
                case 'exchangeRate':
                    row.NewCurrencyExchangeRate = validation.value;
                    break;
            }
        }

        // Force grid update if gridApi is available and has setValue method
        // This only works in grid event contexts, not in cell renderer contexts
        if (props?.api && props?.node && typeof props.api.setValue === 'function') {
            // Update the cell value in the grid
            props.api.setValue(props.node, props.colDef?.field, validation.value);

            // Refresh the specific cell
            props.api.refreshCells({
                rowNodes: [props.node],
                columns: [props.colDef?.field],
                force: true
            });
        }

        return false;
    }

    // If value was trimmed, update the field with trimmed value
    if (validation.wasTrimmed) {
        const row = props?.valueFormatted ? props.valueFormatted : props?.data;
        if (row) {
            switch (type) {
                case 'basicRate':
                    row.NewBasicRate = validation.value;
                    row.NewBasicRatePerUOM = validation.value;
                    break;
                case 'scrapRate':
                    row.NewScrapRate = validation.value;
                    row.NewScrapRatePerScrapUOM = validation.value;
                    break;
                case 'percentage':
                    row.Percentage = validation.value;
                    break;
                case 'machineRate':
                    row.NewMachineRate = validation.value;
                    break;
                case 'exchangeRate':
                    row.NewCurrencyExchangeRate = validation.value;
                    break;
            }
        }

        // Force grid update with trimmed value
        if (props?.api && props?.node && typeof props.api.setValue === 'function') {
            props.api.setValue(props.node, props.colDef?.field, validation.value);
            props.api.refreshCells({
                rowNodes: [props.node],
                columns: [props.colDef?.field],
                force: true
            });
        }
    }

    return true;
};

/**
 * Enhanced value setter for grid cells that validates and resets invalid values
 * @param {Object} params - Grid value setter parameters
 * @param {string} type - Field type
 * @returns {boolean} - Whether the value was set successfully
 */
export const valueSetter = (params, type) => {
    const { newValue, data, node, colDef, api } = params;

    // Validate the new value
    const validation = validateRevisedRate(newValue, { data, node, colDef, api }, type);

    // Show toast message for both errors and warnings (trimmed values)
    if (validation.message) {
        Toaster.warning(validation.message);
    }

    if (!validation.isValid) {
        // Reset to last valid value
        const lastValidValue = getLastValidValue(data, type);

        // Update the data
        switch (type) {
            case 'basicRate':
                data.NewBasicRate = lastValidValue;
                data.NewBasicRatePerUOM = lastValidValue; // Also update NewBasicRatePerUOM
                data.NewBasicrateFromPercentage = undefined;
                data.Percentage = 0;
                break;
            case 'scrapRate':
                data.NewScrapRate = lastValidValue;
                // Also update NewScrapRatePerScrapUOM if IsScrapUOMApply is 'Yes'
                if (data.IsScrapUOMApply === 'Yes' && data.CalculatedFactor) {
                    data.NewScrapRatePerScrapUOM = lastValidValue / data.CalculatedFactor;
                }
                break;
            case 'percentage':
                data.Percentage = lastValidValue;
                break;
            case 'machineRate':
                data.NewMachineRate = lastValidValue;
                break;
            case 'exchangeRate':
                data.NewCurrencyExchangeRate = lastValidValue;
                break;
            case 'operationRate':
                data.NewRate = lastValidValue;
                break;
            case 'operationBasicRate':
                data.NewOperationBasicRate = lastValidValue;
                break;
        }

        // Force grid refresh
        if (api) {
            api.refreshCells({
                rowNodes: [node],
                columns: [colDef.field],
                force: true
            });
        }

        return false; // Reject the invalid value
    }

    // Set the valid or trimmed value
    switch (type) {
        case 'basicRate':
            data.NewBasicRate = validation.value;
            data.NewBasicRatePerUOM = validation.value; // Also update NewBasicRatePerUOM
            break;
        case 'scrapRate':
            data.NewScrapRate = validation.value;
            // Also update NewScrapRatePerScrapUOM if IsScrapUOMApply is 'Yes'
            if (data.IsScrapUOMApply === 'Yes' && data.CalculatedFactor) {
                data.NewScrapRatePerScrapUOM = validation.value / data.CalculatedFactor;
            }
            break;
        case 'percentage':
            data.Percentage = validation.value;
            break;
        case 'machineRate':
            data.NewMachineRate = validation.value;
            break;
        case 'exchangeRate':
            data.NewCurrencyExchangeRate = validation.value;
            break;
        case 'operationRate':
            data.NewRate = validation.value;
            break;
        case 'operationBasicRate':
            data.NewOperationBasicRate = validation.value;
            break;
    }

    // If value was trimmed, force grid refresh to show trimmed value
    if (validation.wasTrimmed && api) {
        api.refreshCells({
            rowNodes: [node],
            columns: [colDef.field],
            force: true
        });
    }

    return true; // Accept the valid or trimmed value
};

/**
 * Validate and format a value for display
 * @param {string|number} value - Value to validate and format
 * @param {Object} props - Cell props
 * @param {string} type - Field type
 * @returns {Object} - Formatted value and validation result
 */
export const validateAndFormatValue = (value, props, type) => {
    const validation = validateRevisedRate(value, props, type);
    
    if (!validation.isValid) {
        return {
            value: validation.value,
            isValid: false,
            message: validation.message
        };
    }
    
    return {
        value: validation.value,
        isValid: true,
        message: ''
    };
};

/**
 * Check if a value has changed from its original value
 * @param {Object} row - Row data
 * @param {string} type - Field type
 * @param {number} newValue - New value to check
 * @returns {boolean} - Whether the value has changed
 */
export const hasValueChanged = (row, type, newValue) => {
    let originalValue;
    
    switch (type) {
        case 'basicRate':
            originalValue = Number(row?.BasicRatePerUOM || 0);
            break;
        case 'scrapRate':
            originalValue = Number(row?.ScrapRate || 0);
            break;
        default:
            return false;
    }
    
    return Math.abs(Number(newValue) - originalValue) > 0.0001; // Use small epsilon for floating point comparison
};

/**
 * Reset field to original value
 * @param {Object} row - Row data
 * @param {string} type - Field type
 */
export const resetFieldToOriginal = (row, type) => {
    switch (type) {
        case 'basicRate':
            row.NewBasicRate = undefined;
            row.NewBasicrateFromPercentage = undefined;
            row.Percentage = 0;
            break;
        case 'scrapRate':
            row.NewScrapRate = undefined;
            break;
        case 'percentage':
            row.Percentage = 0;
            break;
        case 'machineRate':
            row.NewMachineRate = undefined;
            break;
        case 'exchangeRate':
            row.NewCurrencyExchangeRate = undefined;
            break;
    }
};

/**
 * Reset field to last valid value and update grid
 * @param {Object} row - Row data
 * @param {string} type - Field type
 * @param {Object} props - Cell props
 * @param {Object} gridApi - Grid API instance
 * @param {Object} list - List array for state updates
 * @param {number} rowIndex - Row index in the list
 */
export const resetFieldToLastValidValue = (row, type, props, gridApi, list, rowIndex) => {
    const lastValidValue = getLastValidValue(row, type);
    
    
    // Update row data
    switch (type) {
        case 'basicRate':
            row.NewBasicRate = lastValidValue;
            row.NewBasicrateFromPercentage = undefined;
            row.Percentage = 0;
            break;
        case 'scrapRate':
            row.NewScrapRate = lastValidValue;
            // Also update NewScrapRatePerScrapUOM if IsScrapUOMApply is 'Yes'
            if (row.IsScrapUOMApply === 'Yes' && row.CalculatedFactor) {
                row.NewScrapRatePerScrapUOM = lastValidValue / row.CalculatedFactor;
            }
            break;
        case 'percentage':
            row.Percentage = lastValidValue;
            break;
        case 'machineRate':
            row.NewMachineRate = lastValidValue;
            break;
        case 'exchangeRate':
            row.NewCurrencyExchangeRate = lastValidValue;
            break;
        case 'operationRate':
            row.NewRate = lastValidValue;
            break;
        case 'operationBasicRate':
            row.NewOperationBasicRate = lastValidValue;
            break;
    }
    
    // Update list array if provided
    if (list && rowIndex !== undefined) {
        const listItem = list[rowIndex];
        if (listItem) {
            switch (type) {
                case 'basicRate':
                    listItem.NewBasicRate = lastValidValue;
                    listItem.NewBasicrateFromPercentage = undefined;
                    listItem.Percentage = 0;
                    break;
                case 'scrapRate':
                    listItem.NewScrapRate = lastValidValue;
                    // Also update NewScrapRatePerScrapUOM if IsScrapUOMApply is 'Yes'
                    if (listItem.IsScrapUOMApply === 'Yes' && listItem.CalculatedFactor) {
                        listItem.NewScrapRatePerScrapUOM = lastValidValue / listItem.CalculatedFactor;
                    }
                    break;
                case 'percentage':
                    listItem.Percentage = lastValidValue;
                    break;
                case 'machineRate':
                    listItem.NewMachineRate = lastValidValue;
                    break;
                case 'exchangeRate':
                    listItem.NewCurrencyExchangeRate = lastValidValue;
                    break;
                case 'operationRate':
                    listItem.NewRate = lastValidValue;
                    break;
                case 'operationBasicRate':
                    listItem.NewOperationBasicRate = lastValidValue;
                    break;
            }
        }
    }
    
    // Update grid display
    if (gridApi && props?.node) {
        const fieldName = getFieldNameForType(type);
        if (fieldName) {
            // Update the cell value in the grid
            gridApi.setValue(props.node, fieldName, lastValidValue);
            
            // Refresh the specific cell
            gridApi.refreshCells({
                rowNodes: [props.node],
                columns: [fieldName],
                force: true
            });
            
            // Also try to redraw the row to ensure the change is visible
            gridApi.redrawRows({ rowNodes: [props.node] });
        }
    }
};

/**
 * Get field name for a given type
 * @param {string} type - Field type
 * @returns {string} - Field name
 */
const getFieldNameForType = (type) => {
    switch (type) {
        case 'basicRate':
            return 'NewBasicRate';
        case 'scrapRate':
            return 'NewScrapRate';
        case 'percentage':
            return 'Percentage';
        case 'machineRate':
            return 'NewMachineRate';
        case 'exchangeRate':
            return 'NewCurrencyExchangeRate';
        case 'operationRate':
            return 'NewRate';
        case 'operationBasicRate':
            return 'NewOperationBasicRate';
        default:
            return null;
    }
};

export default {
    validateRevisedRate,
    beforeSaveCell,
    validateAndFormatValue,
    hasValueChanged,
    resetFieldToOriginal,
    resetFieldToLastValidValue,
    valueSetter
};